import { fetchProductSubmissionStatuses } from '@common/services/productService';
import { useQuery } from '@tanstack/react-query';
import { SubmissionStatus as SubmissionStatusEnum } from '@common/types';
import useAppNavigation from '../../../routing/useAppNavigation';
import { fetchPublicProductGroupState } from '@features/productGroup/services/productGroupService';
import { IPublicProductGroupState } from '@common/features/productGroup/types';
import { SubmissionStatus, IProductSubmissionStatusDto } from '@services/types';

export interface IProductGroupHistoryElement {
  title: string;
  status: SubmissionStatus;
  submissionId?: number;
  productGroupId?: number;
}

interface IUseProductGroupHistory {
  productGroupId: number;
  activeSubmissions?: IProductSubmissionStatusDto[];
}

export const useProductGroupHistory = ({ productGroupId, activeSubmissions }: IUseProductGroupHistory) => {
  const { goToProductGroup, goToPublishedProductGroup } = useAppNavigation();

  const { data } = useQuery<IProductSubmissionStatusDto[]>(
    ['fetchProductSubmissionStatuses', productGroupId],
    () => fetchProductSubmissionStatuses(productGroupId, SubmissionStatusEnum.Active),
    { enabled: !!productGroupId && !activeSubmissions },
  );

  const { data: productGroup } = useQuery<IPublicProductGroupState>(
    ['fetchPublicProductGroupState', productGroupId],
    () => fetchPublicProductGroupState(productGroupId),
    { enabled: !!productGroupId },
  );

  const onClick = (productGroupHistoryElement: IProductGroupHistoryElement) => {
    if (productGroupHistoryElement.submissionId != null) {
      goToProductGroup(productGroupHistoryElement.submissionId.toString());
      return;
    }

    goToPublishedProductGroup(productGroupHistoryElement.productGroupId!.toString());
  };

  const createProductGroupHistoryElements = () => {
    const submissionData = activeSubmissions || data;
    
    const submissionElements: IProductGroupHistoryElement[] =
      submissionData?.map((x) => ({ title: x.title, status: x.status, submissionId: x.submissionId } as IProductGroupHistoryElement)) ?? [];

    if (productGroup != null) {
      submissionElements.push({
        title: productGroup.productGroup.productGroupName,
        status: SubmissionStatus.Published,
        productGroupId: productGroup.productGroupId,
      } as IProductGroupHistoryElement);
    }

    return submissionElements;
  };

  const productGroupElements = createProductGroupHistoryElements();

  return {
    productGroupElements,
    onClick,
  };
};
